<template>
  <div v-if="!showSmsCodeAuth" class="topup" :class="{ 'topup-dark': isDarkMode }">
    <div v-if="alert.message" class="pay-alerts">
      <argon-alert
        v-for="msg in alert.message"
        :key="msg"
        class="text-sm text-center mb-2"
        :color="alert.type"
      >
        {{ msg }}
      </argon-alert>
    </div>

    <form @submit.prevent="WithDrawCode">
      <div class="pay-grid">
        <div class="pay-chip pay-chip-iban">
          <div v-if="hasManyAccounts" class="account-picker">
            <button
              type="button"
              class="account-picker-btn"
              @click="toggleAccountMenu"
            >
              <span>{{ credit.BankAccount?.Account || "Vyber účet" }}</span>
              <fa-icon icon="fa-solid fa-chevron-down" class="account-caret" :class="{ 'is-open': accountMenuOpen }" />
            </button>
            <div v-if="accountMenuOpen" class="account-menu">
              <button
                v-for="(data, index) in accountList"
                :key="data.Account || index"
                type="button"
                class="account-menu-item"
                :class="{ 'is-active': credit.BankAccount?.Account === data.Account }"
                @click="selectAccount(data)"
              >
                {{ data.Account }}
              </button>
            </div>
          </div>
          <span v-else class="pay-value pay-value-accent">{{ credit.BankAccount?.Account }}</span>
          <span class="pay-label">Číslo účtu</span>
          <span v-if="v$.BankAccount.$error" class="pay-error">
            {{ v$.BankAccount.$errors[0].$message }}
          </span>
        </div>

        <div v-if="credit.BankAccount" class="pay-chip">
          <span class="pay-value pay-value-accent">{{ credit.BankAccount.AccountName || "—" }}</span>
          <span class="pay-label">Název účtu</span>
        </div>
        <div v-if="credit.BankAccount" class="pay-chip pay-chip-vs">
          <span class="pay-value pay-value-accent">{{ credit.BankAccount.AccountBankName || "—" }}</span>
          <span class="pay-label">Banka</span>
        </div>

        <div class="pay-chip pay-chip-amount">
          <div class="pay-stepper">
            <button
              type="button"
              class="pay-stepper-btn pay-stepper-btn-jump"
              :disabled="amountNum <= jumpStep"
              :title="'−' + jumpStep"
              @click="nudge(-jumpStep)"
            >
              <fa-icon icon="fa-solid fa-minus" />
              <span class="jump-num">{{ jumpStep }}</span>
            </button>
            <button
              type="button"
              class="pay-stepper-btn"
              :disabled="amountNum <= minAmount"
              title="Méně"
              @click="nudge(-stepAmount)"
            >
              <fa-icon icon="fa-solid fa-minus" />
            </button>
            <input
              class="pay-amount-input"
              type="text"
              inputmode="decimal"
              name="amount"
              placeholder="Částka"
              autocomplete="off"
              :value="credit.Amount"
              @input="onAmountInput"
              @blur="onAmountBlur"
            />
            <button
              type="button"
              class="pay-stepper-btn"
              :disabled="amountNum >= maxAmount"
              title="Více"
              @click="nudge(stepAmount)"
            >
              <fa-icon icon="fa-solid fa-plus" />
            </button>
            <button
              type="button"
              class="pay-stepper-btn pay-stepper-btn-jump"
              :disabled="amountNum + jumpStep > maxAmount"
              :title="'+' + jumpStep"
              @click="nudge(jumpStep)"
            >
              <fa-icon icon="fa-solid fa-plus" />
              <span class="jump-num">{{ jumpStep }}</span>
            </button>
            <button
              type="button"
              class="pay-stepper-btn pay-stepper-btn-preset"
              :disabled="amountNum >= maxAmount"
              title="Maximum"
              @click="nudgeToMax"
            >
              MAX
            </button>
          </div>
          <span class="pay-label">Částka (max. {{ maxAmountLabel }})</span>
          <span v-if="v$.Amount.$error" class="pay-error">
            {{ v$.Amount.$errors[0].$message }}
          </span>
        </div>
      </div>

      <argon-button
        class="pay-submit"
        type="submit"
        color="primary"
        size="lg"
        :disabled="isButtonLoading"
        fullWidth
      >
        <span v-if="isButtonLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        Požádat o výběr
      </argon-button>
    </form>
  </div>
  <div v-else class="topup" :class="{ 'topup-dark': isDarkMode }">
    <sms-auth-form :user-phone="userPhone" @withdraw-success="emit('withdraw-credit-success')" />
  </div>
</template>
<script setup>
import { ref, reactive, computed, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { required, helpers, between } from "@vuelidate/validators";
import dataService from "@/services/dataService";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonAlert from "@/components/ArgonAlert.vue";
import SmsAuthForm from "./userAuth/SmsAuthForm.vue";

const props = defineProps({
  withDrawData: Object
});
const emit = defineEmits(["withdraw-credit-success"]);
const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);

const initialStateCredit = {
  BankAccount: null,
  Amount: null
};

const credit = reactive({ ...initialStateCredit });
const showSmsCodeAuth = ref(false);
const userPhone = ref(null);

const accountList = computed(() => {
  const raw = props.withDrawData?.BankAccount;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") return [raw];
  return [];
});
const hasManyAccounts = computed(() => accountList.value.length > 1);
const accountMenuOpen = ref(false);

watch(accountList, (list) => {
  if (list.length === 1) credit.BankAccount = list[0];
}, { immediate: true });

const toggleAccountMenu = () => {
  accountMenuOpen.value = !accountMenuOpen.value;
};

const selectAccount = (account) => {
  credit.BankAccount = account;
  accountMenuOpen.value = false;
  v$.value.BankAccount.$touch();
};

const CreditMax = computed(() => props.withDrawData.CreditMax);
const alert = computed(() => store.state.alert);
const isButtonLoading = computed(() => store.getters["loadingModule/isButtonLoading"]);
const maxAmountLabel = computed(() => store.getters.formatNumber(CreditMax.value));
const minAmount = 1;
const maxAmount = computed(() => Number(CreditMax.value) || 0);
const stepAmount = 10;
const jumpStep = computed(() => {
  const max = maxAmount.value;
  if (max > 1000) return 100;
  if (max > 100) return 10;
  return 10;
});
const amountNum = computed(() => {
  const n = Number(credit.Amount);
  return Number.isFinite(n) ? n : 0;
});

const rules = computed(() => ({
  BankAccount: {
    required: helpers.withMessage("Vyber bankovní účet", required),
  },
  Amount: {
    between: helpers.withMessage(`Zadej částku v rozmezí od 1 do ${CreditMax.value.toLocaleString()}`, between(1, CreditMax)),
    required: helpers.withMessage("Zadej částku pro výběr kreditu", required),
  },
}));

const v$ = useVuelidate(rules, credit);

const resetForm = () => {
  v$.value.$reset();
  Object.assign(credit, initialStateCredit);
};

const setCreditAmount = (value, inputEl) => {
  if (value === "" || value === null) {
    credit.Amount = null;
    if (inputEl) inputEl.value = "";
    return;
  }
  const n = Number(String(value).replace(",", "."));
  if (!Number.isFinite(n)) {
    credit.Amount = value;
    return;
  }
  let next = value;
  if (n > maxAmount.value) next = String(maxAmount.value);
  else if (n < 0) next = String(minAmount);
  credit.Amount = next;
  if (inputEl && inputEl.value !== String(next)) inputEl.value = String(next);
};

const onAmountInput = (event) => {
  setCreditAmount(event.target.value, event.target);
  v$.value.Amount.$touch();
};

const onAmountBlur = (event) => {
  setCreditAmount(event.target.value, event.target);
  v$.value.Amount.$touch();
};

const nudge = (delta) => {
  const next = Math.min(maxAmount.value, Math.max(minAmount, amountNum.value + delta));
  setCreditAmount(String(next));
  v$.value.Amount.$touch();
};

const nudgeToMax = () => {
  setCreditAmount(String(maxAmount.value));
  v$.value.Amount.$touch();
};

const WithDrawCode = async () => {
  setCreditAmount(credit.Amount);
  const isFormCorrect = await v$.value.$validate();

  if (!isFormCorrect) return;

  try {
    store.dispatch("loadingModule/setButtonLoading", true);
    const response = await dataService.WithDrawCode(store.state.user.UserID, store.state.token, credit);

    if (response.Result) {
      userPhone.value = response.Data.UserPhone;
      showSmsCodeAuth.value = true;
    } else {
      resetForm();
      store.dispatch("alertError", response.ErrorMessage);
    }
  } catch (error) {
    console.error(error);
  } finally {
    store.dispatch("loadingModule/setButtonLoading", false);
  }
};
</script>

<style scoped>
.topup {
  padding: 0 0.35rem 0.35rem;
}

.pay-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
  margin-bottom: 0.75rem;
}

.pay-chip {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding: 0.55rem 0.7rem;
  border-radius: 0.75rem;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
}

.topup-dark .pay-chip {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(226, 232, 240, 0.18);
}

.pay-chip-iban,
.pay-chip-amount {
  grid-column: 1 / -1;
}

.pay-value {
  font-size: 1.02rem;
  font-weight: 800;
  line-height: 1.25;
  color: #0F172A;
  word-break: break-word;
}

.topup-dark .pay-value {
  color: #FFFFFF;
}

.pay-value-accent {
  color: #EA580C;
}

.topup-dark .pay-value-accent {
  color: #FF8A4C;
}

.account-picker {
  position: relative;
}

.account-picker-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border: 0;
  background: transparent;
  padding: 0;
  font-size: 1.02rem;
  font-weight: 800;
  line-height: 1.25;
  color: #EA580C;
  text-align: left;
  cursor: pointer;
}

.topup-dark .account-picker-btn {
  color: #FF8A4C;
}

.account-caret {
  flex-shrink: 0;
  font-size: 0.7rem;
  color: #94A3B8;
  transition: transform 0.15s ease;
}

.topup-dark .account-caret {
  color: #E2E8F0;
}

.account-caret.is-open {
  transform: rotate(180deg);
}

.account-menu {
  position: absolute;
  z-index: 8;
  left: 0;
  right: 0;
  top: calc(100% + 0.4rem);
  padding: 0.25rem;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 0.75rem;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
}

.topup-dark .account-menu {
  background: #334155;
  border-color: #64748B;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.4);
}

.account-menu-item {
  width: 100%;
  border: 0;
  background: transparent;
  border-radius: 0.55rem;
  padding: 0.5rem 0.65rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: #EA580C;
  text-align: left;
  cursor: pointer;
}

.topup-dark .account-menu-item {
  color: #FF8A4C;
}

.account-menu-item:hover,
.account-menu-item.is-active {
  background: #E2E8F0;
}

.topup-dark .account-menu-item:hover,
.topup-dark .account-menu-item.is-active {
  background: #475569;
}

.pay-chip-amount .pay-stepper {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  width: 100%;
}

.pay-stepper-btn {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: 1.5px solid #CBD5E1;
  border-radius: 50%;
  background: #fff;
  color: #475569;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  font-size: 0.72rem;
  line-height: 1;
}

.pay-stepper-btn:hover:not(:disabled) {
  color: #FF8A4C;
  border-color: #FF8A4C;
}

.pay-stepper-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.pay-stepper-btn-jump {
  font-size: 0.82rem;
  font-weight: 800;
}

.pay-stepper-btn-jump :deep(svg) {
  font-size: 0.82rem;
}

.pay-stepper-btn-preset {
  width: auto;
  min-width: 34px;
  padding: 0 0.35rem;
  border-radius: 999px;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.jump-num {
  font-size: 0.52rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  line-height: 1;
  margin-top: 0.02rem;
}

.topup-dark .pay-stepper-btn {
  background: #334155;
  border-color: #64748B;
  color: #E2E8F0;
}

.pay-chip-amount .pay-amount-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 34px;
  border: 1.5px solid #E2E8F0;
  border-radius: 0.65rem;
  background: #fff;
  box-shadow: none;
  font-size: 0.95rem;
  font-weight: 800;
  color: #EA580C;
  padding: 0 0.45rem;
  outline: none;
  text-align: center;
  -moz-appearance: textfield;
}

.topup-dark .pay-chip-amount .pay-amount-input {
  background: #334155;
  border-color: #64748B;
  color: #FF8A4C;
}

.pay-chip-amount .pay-amount-input:focus {
  border-color: #FF8A4C;
}

.pay-chip-amount .pay-amount-input::placeholder {
  color: #94A3B8;
  font-weight: 700;
  font-size: 0.85rem;
}

.topup-dark .pay-chip-amount .pay-amount-input::placeholder {
  color: #CBD5E1;
}

.pay-chip-amount .pay-amount-input::-webkit-outer-spin-button,
.pay-chip-amount .pay-amount-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.pay-label {
  margin-top: 0.12rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #475569;
}

.topup-dark .pay-label {
  color: #F1F5F9;
}

.pay-error {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #FCA5A5;
}

.pay-error-input {
  margin-bottom: 0.55rem;
}

.pay-submit {
  margin-top: 0.55rem;
}

.pay-alerts {
  margin-bottom: 0.55rem;
}
</style>
